import { useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import Layout from "../layout/Layout";
import { pageRouteUrls } from "../helper/pageRouteUrls";
import { CourseService } from "../service/CourseService";
import { CommonService } from "../service/CommonService";
import CourseCard from "../component/CourseCard";
import { CategoryModel, CourseSummaryModel, UserTypeEnum } from "../generated/api";
import { SubscriptionService } from "../service/SubscriptionService";
import { useCurrUserUtility } from "../contexts/useCurrUserUtility";
import CONFIG_KEYS from "../config";
import "./home.css";

export interface Props {
}

type HowItWorkAudience = "student" | "tutor" | "parent" | "academy";

// Detect the visitor's country (IP-based) → recommended currency, USD fallback.
// Mirrors the detection used on /pricing so the hero "from" price matches the plans.
const detectCountryCode = async (): Promise<string | undefined> => {
    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);
        const res = await fetch("https://ipapi.co/json/", { signal: controller.signal });
        clearTimeout(timeoutId);
        if (res.ok) {
            const data = await res.json();
            if (data?.country_code) return String(data.country_code).toUpperCase();
        }
    } catch {
        /* ignore — falls back to USD */
    }
    return navigator.language?.split("-")[1]?.toUpperCase();
};

// Decorative shapes are purely positional (first/middle/last card) — independent of audience content.
const howItWorkShapes = [
    { className: "shape-1", src: "/assets/images/shape/shape-15.png" },
    { className: "shape-2", src: "/assets/images/shape/shape-15.png" },
    { className: "shape-3", src: "/assets/images/shape/shape-16.png" },
];

const HomePage: React.FC<Props> = props => {

    const { t } = useTranslation();
    const { isAuth } = useCurrUserUtility();

    // Instructor first (paying ICP), Academy right after — both paying audiences lead.
    const howItWorkTabs: { key: HowItWorkAudience; label: string; className: string }[] = [
        { key: "tutor", label: t("home.hiw.tabs.tutor"), className: "tutor" },
        { key: "academy", label: t("home.hiw.tabs.academy"), className: "academy" },
        { key: "student", label: t("home.hiw.tabs.student"), className: "" },
        { key: "parent", label: t("home.hiw.tabs.parent"), className: "parent" },
    ];

    // JTBD journeys for the paying audiences — shown as a dedicated step chain
    // (colored to match the audience tab) instead of the 3-card layout the other
    // audiences use. The academy flow leads with adding tutors.
    const journeys: Partial<Record<HowItWorkAudience, { icon: string; label: string }[]>> = {
        tutor: [
            { icon: "flaticon-open-book", label: t("home.hiw.journey.build") },
            { icon: "flaticon-tutor", label: t("home.hiw.journey.live") },
            { icon: "flaticon-forms", label: t("home.hiw.journey.exam") },
            { icon: "flaticon-transparency", label: t("home.hiw.journey.grade") },
            { icon: "flaticon-badge", label: t("home.hiw.journey.certificate") },
            { icon: "flaticon-increase", label: t("home.hiw.journey.paid") },
        ],
        academy: [
            { icon: "flaticon-tutor", label: t("home.hiw.academyJourney.addTutors") },
            { icon: "flaticon-open-book", label: t("home.hiw.academyJourney.build") },
            { icon: "flaticon-forms", label: t("home.hiw.academyJourney.live") },
            { icon: "flaticon-transparency", label: t("home.hiw.academyJourney.grade") },
            { icon: "flaticon-badge", label: t("home.hiw.academyJourney.certificate") },
            { icon: "flaticon-increase", label: t("home.hiw.academyJourney.paid") },
        ],
    };

    const howItWorkContent: Record<HowItWorkAudience, {
        steps: { icon: string; title: string; desc: string }[];
        badges: { icon: string; text: string }[];
    }> = {
        student: {
            steps: [
                { icon: "flaticon-transparency", title: t("home.hiw.student.s1t"), desc: t("home.hiw.student.s1d") },
                { icon: "flaticon-forms", title: t("home.hiw.student.s2t"), desc: t("home.hiw.student.s2d") },
                { icon: "flaticon-badge", title: t("home.hiw.student.s3t"), desc: t("home.hiw.student.s3d") },
            ],
            badges: [
                { icon: "flaticon-notification", text: t("home.hiw.smsReminder") },
                { icon: "flaticon-form", text: t("home.hiw.student.badge") },
            ],
        },
        tutor: {
            steps: [
                { icon: "flaticon-open-book", title: t("home.hiw.tutor.s1t"), desc: t("home.hiw.tutor.s1d") },
                { icon: "flaticon-tutor", title: t("home.hiw.tutor.s2t"), desc: t("home.hiw.tutor.s2d") },
                { icon: "flaticon-increase", title: t("home.hiw.tutor.s3t"), desc: t("home.hiw.tutor.s3d") },
            ],
            badges: [
                { icon: "flaticon-notification", text: t("home.hiw.smsReminder") },
                { icon: "flaticon-form", text: t("home.hiw.tutor.badge") },
            ],
        },
        parent: {
            steps: [
                { icon: "flaticon-form", title: t("home.hiw.parent.s1t"), desc: t("home.hiw.parent.s1d") },
                { icon: "flaticon-increase", title: t("home.hiw.parent.s2t"), desc: t("home.hiw.parent.s2d") },
                { icon: "flaticon-notification", title: t("home.hiw.parent.s3t"), desc: t("home.hiw.parent.s3d") },
            ],
            badges: [
                { icon: "flaticon-notification", text: t("home.hiw.smsReminder") },
                { icon: "flaticon-form", text: t("home.hiw.parent.badge") },
            ],
        },
        academy: {
            steps: [
                { icon: "flaticon-tutor", title: t("home.hiw.academy.s1t"), desc: t("home.hiw.academy.s1d") },
                { icon: "flaticon-open-book", title: t("home.hiw.academy.s2t"), desc: t("home.hiw.academy.s2d") },
                { icon: "flaticon-forms", title: t("home.hiw.academy.s3t"), desc: t("home.hiw.academy.s3d") },
            ],
            badges: [
                { icon: "flaticon-notification", text: t("home.hiw.smsReminder") },
                { icon: "flaticon-form", text: t("home.hiw.academy.badge") },
            ],
        },
    };
    const [loading, setLoading] = useState<boolean>(false);
    //const [recentCourses, setRecentCourses] = useState<CourseSummaryModel[]>([]);
    // const [categories, setCategories] = useState<CategoryModel[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [howItWorkAudience, setHowItWorkAudience] = useState<HowItWorkAudience>("tutor");
    // Cheapest live-teaching (tutor) plan in the visitor's currency, shown as a hero "from" price.
    const [heroPrice, setHeroPrice] = useState<{ symbol: string; amount: number } | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        // Fetch the lowest tutor-plan price in the visitor's currency (same basis as /pricing).
        const fetchHeroPrice = async () => {
            const service = new SubscriptionService(navigate);
            let currencyCode = "USD";
            try {
                const country = await detectCountryCode();
                if (country) {
                    const rec = await service.getCurrencyForCountry(country);
                    if (rec?.currencyCode) currencyCode = rec.currencyCode;
                }
            } catch {
                /* keep USD */
            }
            try {
                let plans = await service.getPublicPricing(currencyCode);
                if (!plans?.length) plans = await service.getPublicPricing();
                const tutorMonthly = (plans ?? []).filter(
                    p => (p.userType ?? 0) === UserTypeEnum.Tutor && (p.billingCycle ?? "Monthly") === "Monthly"
                );
                const cheapest = tutorMonthly
                    .filter(p => (p.price ?? 0) > 0)
                    .sort((a, b) => (a.price ?? 0) - (b.price ?? 0))[0];
                if (cheapest) {
                    setHeroPrice({ symbol: cheapest.symbol ?? "$", amount: cheapest.price ?? 0 });
                }
            } catch {
                /* no badge on failure — hero still renders */
            }
        };
        fetchHeroPrice();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // const courseService = new CourseService(navigate);
                // // const commonService = new CommonService(navigate);
                // const [courses ] = await Promise.all([
                //     courseService.GetRecent(6),
                //     // commonService.getAllCategories(),
                // ]);
                // setRecentCourses(courses);
                // setCategories(cats);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, []);

    const handleSearch = () => {
        const params = new URLSearchParams();
        if (searchQuery.trim()) params.set("query", searchQuery.trim());
        navigate(`${pageRouteUrls.Search()}?${params.toString()}`);
    };

    return (
        <Layout>

            {/* Slider Start */}
            <div className="section slider-section">

                {/* Slider Shape Start */}
                <div className="slider-shape">
                    <img className="shape-1 animation-round" src="/assets/images/shape/shape-8.png" alt="Shape" />
                </div>
                {/* Slider Shape End */}

                <div className="container">

                    {/* Slider Content Start */}
                    <div className="slider-content">
                        <p className="sub-title">{t("home.slider.subTitle")}</p>
                        <h1 className="main-title"><Trans i18nKey="home.slider.title" components={{ 1: <span /> }} /></h1>
                        <p>{t("home.slider.desc", { app: CONFIG_KEYS.ApplicationTitle })}</p>
                        {!isAuth() && heroPrice && (
                            <div className="slider-price-badge">
                                <i className="flaticon-badge"></i>
                                <span>
                                    {t("home.slider.priceBadge", {
                                        price: `${heroPrice.symbol}${heroPrice.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
                                    })}
                                </span>
                            </div>
                        )}
                        <a className="btn btn-primary btn-hover-dark" href={isAuth() ? pageRouteUrls.CourseAdd() : pageRouteUrls.Register()}>
                            {isAuth() ? t("home.slider.createCourse") : t("home.slider.getStarted")}
                        </a>
                        {!isAuth() && (
                            <>
                                <div className="slider-trust" style={{ marginTop: 14, fontSize: 14, color: "#5f6b7a" }}>
                                    <span>{t("home.slider.payoutLine")}</span>
                                </div>
                                <div className="slider-trust" style={{ marginTop: 6, fontSize: 14, color: "#5f6b7a" }}>
                                    <span>{t("home.slider.trustLine")}</span>
                                    <a href={pageRouteUrls.Search()} style={{ color: "#309255", fontWeight: 600, marginInlineStart: 8 }}>
                                        {t("home.slider.studentLink")}
                                    </a>
                                </div>
                            </>
                        )}
                    </div>
                    {/* Slider Content End */}

                </div>

                {/* Slider Courses Box Start */}
                {/* <div className="slider-courses-box">

                    <img className="shape-1 animation-left" src="/assets/images/shape/shape-5.png" alt="Shape" />

                    <div className="box-content">
                        <div className="box-wrapper">
                            <i className="flaticon-open-book"></i>
                            <span className="count">1,235</span>
                            <p>courses</p>
                        </div>
                    </div>

                    <img className="shape-2" src="/assets/images/shape/shape-6.png" alt="Shape" />

                </div> */}
                {/* Slider Courses Box End */}

                {/* Slider Rating Box Start */}
                {/* <div className="slider-rating-box">

                    <div className="box-rating">
                        <div className="box-wrapper">
                            <span className="count">4.8 <i className="flaticon-star"></i></span>
                            <p>Rating (86K)</p>
                        </div>
                    </div>

                    <img className="shape animation-up" src="/assets/images/shape/shape-7.png" alt="Shape" />

                </div> */}
                {/* Slider Rating Box End */}

                {/* Slider Images Start */}
                <div className="slider-images">
                    <div className="images">
                        <img src="/assets/images/slider/slider-1.png" alt={t("home.slider.imageAlt")} />
                    </div>
                </div>
                {/* Slider Images End */}

                {/* Slider Video Start */}
                <div className="slider-video">
                    <img className="shape-1" src="/assets/images/shape/shape-9.png" alt="Shape" />

                    <div className="video-play">
                        <img src="/assets/images/shape/shape-10.png" alt="Shape" />
                        <a href="https://youtube.com/shorts/BMclOYqWQrA?si=6MukEj7gFCHl-Qxn" className="play video-popup">
                            <i className="flaticon-play"></i>
                        </a>
                    </div>
                </div>
                {/* Slider Video End */}

            </div>
            {/* Slider End */}

            {/* All Courses Start */}
            <div className="section section-padding-02">
                <div className="container">

                    {/* All Courses Top Start */}
                    <div className="courses-top">

                        {/* Section Title Start */}
                        <div className="section-title shape-01">
                            <h2 className="main-title"><Trans i18nKey="home.courses.latest" components={{ 1: <span /> }} values={{ name: CONFIG_KEYS.ApplicationShortName }} /></h2>
                        </div>
                        {/* Section Title End */}

                        {/* Courses Search Start */}
                        <div className="courses-search">
                            <form onSubmit={e => { e.preventDefault(); handleSearch(); }}>
                                <input type="text" placeholder={t("home.courses.searchPlaceholder")} value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)} />
                                <button type="submit"><i className="flaticon-magnifying-glass"></i></button>
                            </form>
                        </div>
                        {/* Courses Search End */}

                    </div>
                    {/* All Courses Top End */}

                    {/* Categories Start */}
                    {/* {categories.length > 0 && (
                        <div className="courses-tabs-menu">
                            <ul className="nav" style={{ flexWrap: "wrap", gap: "10px" }}>
                                {categories.slice(0, 8).map(cat => (
                                    <li key={cat.token}>
                                        <button
                                            type="button"
                                            onClick={() => navigate(pageRouteUrls.SearchByCategory(cat.token, cat.categoryName))}
                                        >
                                            {cat.categoryName}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )} */}
                    {/* Categories End */}

                    {/* All Courses Wrapper Start */}
                    {/* <div className="courses-wrapper">
                        <div className="row">
                            {recentCourses.map((item, index) => (
                                <div key={item.token ?? index} className="col-lg-4 col-md-6">
                          
                                    <CourseCard course={item} setCourse={async (updatedCourse: CourseSummaryModel) => {
                                        if (!recentCourses) return;
                                        const updatedSearchResult = recentCourses.map(item => {
                                            if (item.token === updatedCourse.token) {
                                                return updatedCourse;
                                            }
                                            return item;
                                        });

                                        setRecentCourses(updatedSearchResult);
                                    }} />
                          
                                </div>
                            ))}
                            {!loading && recentCourses.length === 0 && (
                                <div className="col-12 text-center py-5">
                                    <p>{t("home.courses.none")}</p>
                                </div>
                            )}
                        </div>
                    </div> */}
                    {/* All Courses Wrapper End */}

                    {/* All Courses Button Start */}
                    <div className="courses-btn text-center">
                        <a href={pageRouteUrls.Search()} className="btn btn-secondary btn-hover-primary">{t("home.courses.browseAll")}</a>
                    </div>
                    {/* All Courses Button End */}

                </div>
            </div>
            {/* All Courses End */}

            {/* Call to Action Start */}
            <div className="section section-padding-02">
                <div className="container">

                    {/* Call to Action Wrapper Start */}
                    <div className="call-to-action-wrapper">

                        <img className="cat-shape-01 animation-round" src="/assets/images/shape/shape-12.png" alt="Shape" />
                        <img className="cat-shape-02" src="/assets/images/shape/shape-13.svg" alt="Shape" />
                        <img className="cat-shape-03 animation-round" src="/assets/images/shape/shape-12.png" alt="Shape" />

                        <div className="row align-items-center">
                            <div className="col-md-6">

                                {/* Section Title Start */}
                                <div className="section-title shape-02">
                                    <p className="sub-title">{t("home.cta.subTitle")}</p>
                                    <h2 className="main-title"><Trans i18nKey="home.cta.title" components={{ 1: <span /> }} values={{ app: CONFIG_KEYS.ApplicationTitle }} /></h2>
                                </div>
                                {/* Section Title End */}

                            </div>
                            <div className="col-md-6">
                                <div className="call-to-action-btn">
                                    <a className="btn btn-primary btn-hover-dark" href={pageRouteUrls.Register()}>{t("home.cta.becomeInstructor")}</a>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Call to Action Wrapper End */}

                </div>
            </div>
            {/* Call to Action End */}

            {/* How It Work Start */}
            <div className="section section-padding mt-n1">
                <div className="container">

                    {/* Section Title Start */}
                    <div className="section-title shape-03 text-center">
                        <p className="sub-title">{t("home.hiw.simple")}</p>
                        <h2 className="main-title"><Trans i18nKey="home.hiw.title" components={{ 1: <span /> }} /></h2>
                    </div>
                    {/* Section Title End */}

                    {/* How it Work Audience Toggle Start */}
                    <div className="hiw-toggle">
                        {howItWorkTabs.map(tab => (
                            <button
                                key={tab.key}
                                type="button"
                                className={`hiw-toggle-btn ${tab.className} ${howItWorkAudience === tab.key ? "active" : ""}`}
                                onClick={() => setHowItWorkAudience(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    {/* How it Work Audience Toggle End */}

                    {/* How it Work Wrapper Start */}
                    {journeys[howItWorkAudience] ? (
                        /* JTBD journey (instructor / academy), colored to match the audience tab */
                        <div className={`hiw-journey ${howItWorkAudience}`}>
                            {journeys[howItWorkAudience]!.map((stage, index) => (
                                <React.Fragment key={stage.label}>
                                    {index > 0 && <span className="hiw-journey-arrow" aria-hidden="true">›</span>}
                                    <div className="hiw-journey-step">
                                        <span className="hiw-journey-icon"><i className={stage.icon}></i></span>
                                        <span className="hiw-journey-label">{stage.label}</span>
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    ) : (
                        <div className="how-it-work-wrapper">
                            {howItWorkContent[howItWorkAudience].steps.map((step, index) => (
                                <React.Fragment key={step.title}>
                                    {index > 0 && (
                                        <div className="work-arrow">
                                            <img className="arrow" src="/assets/images/shape/shape-17.png" alt="Shape" />
                                        </div>
                                    )}
                                    <div className="single-work">
                                        <img
                                            className={howItWorkShapes[index].className}
                                            src={howItWorkShapes[index].src}
                                            alt="Shape"
                                        />
                                        <div className="work-icon">
                                            <i className={step.icon}></i>
                                        </div>
                                        <div className="work-content">
                                            <h3 className="title">{step.title}</h3>
                                            <p>{step.desc}</p>
                                        </div>
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                    {/* How it Work Wrapper End */}

                    {/* How it Work Badges Start */}
                    <div className="hiw-badges">
                        {howItWorkContent[howItWorkAudience].badges.map(badge => (
                            <span key={badge.text} className={`hiw-badge ${howItWorkAudience !== "student" ? howItWorkAudience : ""}`}>
                                <i className={badge.icon}></i> {badge.text}
                            </span>
                        ))}
                    </div>
                    {/* How it Work Badges End */}

                </div>
            </div>
            {/* How It Work End */}

        </Layout>
    )
}
export default HomePage
